import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
    TablePagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    TextField,
    InputAdornment,
    Snackbar,
    Checkbox,
    DialogContentText,
} from '@mui/material';
import {
    IconMail,
    IconRefresh,
    IconSend,
    IconCheck,
    IconX,
    IconUserCheck,
    IconUserX,
    IconExclamationCircle,
    IconCircleCheck,
    IconDownload,
    IconCopy,
    IconLink,
    IconDots,
    IconSearch,
} from '@tabler/icons';
import MainCard from 'ui-component/cards/MainCard';
import { useRef } from 'react';
import * as XLSX from 'xlsx';

const GET_INTERESTED_INVITATIONS = gql`
    query GetInterestedInvitations($eventId: String!, $role: ROLE!) {
        interestedInvitations(eventId: $eventId, role: $role) {
            serial
            data
            isAccepted
            isInvitationSend
            created
            status
            uploadedBy
            uploadedByEmail
            invitationStatus
            responseTimestamp
            isInterested
            isConfirmationSent
        }
        invitationStats(eventId: $eventId, role: $role) {
            total
            sent
            accepted
            rejected
            pending
            allowedRegistrations
            remainingSlots
        }
    }
`;

const ACCEPT_INTERESTED_INVITATION = gql`
    mutation AcceptInterestedInvitation(
        $eventId: String!
        $role: ROLE!
        $invitationIds: [String!]!
    ) {
        acceptInterestedInvitation(eventId: $eventId, role: $role, invitationIds: $invitationIds) {
            success
            totalSent
            totalFailed
            message
        }
    }
`;

const REJECT_INTERESTED_INVITATION = gql`
    mutation RejectInterestedInvitation(
        $eventId: String!
        $role: ROLE!
        $invitationIds: [String!]!
        $sendEmail: Boolean!
    ) {
        rejectInterestedInvitation(
            eventId: $eventId
            role: $role
            invitationIds: $invitationIds
            sendEmail: $sendEmail
        ) {
            success
            totalSent
            totalFailed
            message
        }
    }
`;

const GET_ACCEPT_EMAIL_TEMPLATE = gql`
    query GetAcceptEmailTemplate($eventId: String!, $role: ROLE!) {
        acceptEmailTemplate(eventId: $eventId, role: $role) {
            subject
            body
            isHtml
        }
    }
`;

const GET_REJECT_EMAIL_TEMPLATE = gql`
    query GetRejectEmailTemplate($eventId: String!, $role: ROLE!) {
        rejectEmailTemplate(eventId: $eventId, role: $role) {
            subject
            body
            isHtml
        }
    }
`;

export default function InterestedInvitations({ eventId, role }) {
    const [selectedInvitations, setSelectedInvitations] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [sendRejectEmail, setSendRejectEmail] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewAction, setPreviewAction] = useState(''); // 'accept' or 'reject'

    const { data, loading, error, refetch } = useQuery(GET_INTERESTED_INVITATIONS, {
        variables: { eventId, role },
    });

    const { data: acceptTemplateData } = useQuery(GET_ACCEPT_EMAIL_TEMPLATE, {
        variables: { eventId, role },
    });

    const { data: rejectTemplateData } = useQuery(GET_REJECT_EMAIL_TEMPLATE, {
        variables: { eventId, role },
    });

    const [acceptInvitations, { loading: accepting }] = useMutation(ACCEPT_INTERESTED_INVITATION, {
        onCompleted: (data) => {
            setToast({
                open: true,
                message: data.acceptInterestedInvitation.message,
                severity: 'success',
            });
            setSelectedInvitations([]);
            refetch();
        },
        onError: (e) => {
            setToast({
                open: true,
                message: e.message || 'Failed to send registration links',
                severity: 'error',
            });
        },
    });

    const [rejectInvitations, { loading: rejecting }] = useMutation(REJECT_INTERESTED_INVITATION, {
        onCompleted: (data) => {
            setToast({
                open: true,
                message: data.rejectInterestedInvitation.message,
                severity: 'success',
            });
            setSelectedInvitations([]);
            setRejectDialogOpen(false);
            refetch();
        },
        onError: (e) => {
            setToast({
                open: true,
                message: e.message || 'Failed to reject invitations',
                severity: 'error',
            });
        },
    });
    const getDisplayStatus = (invitation) => {
        // If user has completed registration (final accepted)
        if (invitation.invitationStatus === 'accepted') {
            return 'accepted';
        }
        // If user was rejected
        if (invitation.invitationStatus === 'rejected') {
            return 'rejected';
        }
        // If registration email was sent
        if (invitation.isConfirmationSent) {
            return 'accepted_email_sent';
        }
        // Default pending state
        return 'pending';
    };
    // Get interested invitations data from query result
    const interestedInvitations = data?.interestedInvitations || [];

    // Filter and search logic
    const filteredData = interestedInvitations.filter((invitation) => {
        const invitationInfo = JSON.parse(invitation.data || '{}');

        // Search filter
        const searchMatch =
            searchTerm === '' ||
            Object.values(invitationInfo).some((value) =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            invitation.uploadedByEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            invitation.uploadedBy?.toLowerCase().includes(searchTerm.toLowerCase());

        // Status filter
        const displayStatus = getDisplayStatus(invitation);
        const statusMatch = statusFilter === 'all' || displayStatus === statusFilter;

        return searchMatch && statusMatch;
    });

    // Pagination logic
    const paginatedData =
        rowsPerPage === 'all'
            ? filteredData
            : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [searchTerm, statusFilter]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedInvitations(paginatedData.map((invitation) => invitation.serial));
        } else {
            setSelectedInvitations([]);
        }
    };

    const handleSelectOne = (event, invitationSerial) => {
        if (event.target.checked) {
            setSelectedInvitations([...selectedInvitations, invitationSerial]);
        } else {
            setSelectedInvitations(selectedInvitations.filter((id) => id !== invitationSerial));
        }
    };

    const handleAccept = async () => {
        if (selectedInvitations.length === 0) {
            setToast({
                open: true,
                message: 'Please select at least one invitation to send registration link',
                severity: 'warning',
            });
            return;
        }

        // Check if accept email template exists
        const template = acceptTemplateData?.acceptEmailTemplate;
        if (!template || !template.subject || !template.body) {
            setToast({
                open: true,
                message: 'Please setup Accept Email template first to send registration links.',
                severity: 'error',
            });
            return;
        }

        // Get selected invitation data
        const selectedInvitationData = interestedInvitations.filter((invitation) =>
            selectedInvitations.includes(invitation.serial)
        );

        const emailAddresses = selectedInvitationData
            .map((invitation) => {
                const invitationInfo = JSON.parse(invitation.data || '{}');
                return invitationInfo.Email;
            })
            .filter((email) => email);

        if (emailAddresses.length === 0) {
            setToast({
                open: true,
                message: 'No valid email addresses found in selected invitations',
                severity: 'error',
            });
            return;
        }

        // Show preview confirmation modal
        setPreviewData({
            subject: template.subject,
            body: template.body,
            isHtml: template.isHtml,
            recipientEmails: emailAddresses,
            totalRecipients: emailAddresses.length,
        });
        setPreviewAction('accept');
        setShowPreview(true);
    };

    const handleConfirmAccept = async () => {
        if (!previewData) return;

        try {
            await acceptInvitations({
                variables: {
                    eventId,
                    role,
                    invitationIds: selectedInvitations,
                },
            });
            setShowPreview(false);
            setSelectedInvitations([]);
            refetch();
        } catch (error) {
            console.error('Error accepting invitations:', error);
        }
    };

    const handleConfirmReject = async () => {
        if (!previewData) return;

        try {
            await rejectInvitations({
                variables: {
                    eventId,
                    role,
                    invitationIds: selectedInvitations,
                    sendEmail: sendRejectEmail,
                },
            });
            setShowPreview(false);
            setSelectedInvitations([]);
            setRejectDialogOpen(false);
            refetch();
        } catch (error) {
            console.error('Error rejecting invitations:', error);
        }
    };

    const handleReject = () => {
        if (selectedInvitations.length === 0) {
            setToast({
                open: true,
                message: 'Please select at least one invitation to reject',
                severity: 'warning',
            });
            return;
        }

        // Check if reject email template exists
        const template = rejectTemplateData?.rejectEmailTemplate;
        if (!template || !template.subject || !template.body) {
            setToast({
                open: true,
                message: 'Please setup Reject Email template first to send reject emails.',
                severity: 'error',
            });
            return;
        }

        // Get selected invitation data
        const selectedInvitationData = interestedInvitations.filter((invitation) =>
            selectedInvitations.includes(invitation.serial)
        );

        const emailAddresses = selectedInvitationData
            .map((invitation) => {
                const invitationInfo = JSON.parse(invitation.data || '{}');
                return invitationInfo.Email;
            })
            .filter((email) => email);

        if (emailAddresses.length === 0) {
            setToast({
                open: true,
                message: 'No valid email addresses found in selected invitations',
                severity: 'error',
            });
            return;
        }

        // Show preview confirmation modal
        setPreviewData({
            subject: template.subject,
            body: template.body,
            isHtml: template.isHtml,
            recipientEmails: emailAddresses,
            totalRecipients: emailAddresses.length,
        });
        setPreviewAction('reject');
        setShowPreview(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'accepted':
                return 'success';
            case 'rejected':
                return 'error';
            case 'accepted_email_sent':
                return 'info';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'accepted':
                return <IconCheck size={16} />;
            case 'rejected':
                return <IconX size={16} />;
            case 'accepted_email_sent':
                return <IconMail size={16} />;
            case 'pending':
                return <IconExclamationCircle size={16} />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <LinearProgress />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading interested invitations...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (error) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">
                        Error loading interested invitations: {error.message}
                    </Alert>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard>
            <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Accepted Invitations
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage users who have accepted the invitation. You can send them
                            registration links or reject them.
                        </Typography>
                    </Box>
                    {/* Row 1: Enhanced Comprehensive Insights Cards (md screens and above) */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                        height: '120px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconUserCheck size={28} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.total || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    Total Invitations
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)',
                                        height: '120px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconMail size={28} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.sent || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    Invitations Sent
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(67, 233, 123, 0.3)',
                                        height: '120px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconCheck size={28} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.accepted || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    Accepted
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(250, 112, 154, 0.3)',
                                        height: '120px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconX size={28} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.rejected || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    Rejected
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                                        color: '#333',
                                        boxShadow: '0 4px 20px rgba(168, 237, 234, 0.3)',
                                        height: '120px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconExclamationCircle size={28} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: '#333',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.pending || 0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(51, 51, 51, 0.7)',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    Pending
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                                        color: '#333',
                                        boxShadow: '0 4px 20px rgba(252, 182, 159, 0.3)',
                                        height: '120px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconUserCheck size={28} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: '#333',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {filteredData.length}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(51, 51, 51, 0.7)',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    Interested
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Enhanced Registration Capacity Cards */}
                        {/* <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6} md={6}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(67, 233, 123, 0.3)',
                                        height: '100px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={3}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconCheck size={32} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h3"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.allowedRegistrations ||
                                                        0}
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                                >
                                                    Allowed Registrations
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)',
                                        height: '100px',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={3}
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconExclamationCircle size={32} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h3"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {data?.invitationStats?.remainingSlots || 0}
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                                >
                                                    Remaining Slots
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid> */}
                    </Box>
                    {/* Row 2: Search and Filter */}
                    <Card>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        placeholder="Search invitations..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconSearch size={20} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            label="Status"
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <MenuItem value="all">All Status</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="accepted_email_sent">
                                                Registration Link Sent
                                            </MenuItem>
                                            <MenuItem value="accepted">Accepted</MenuItem>
                                            <MenuItem value="rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Row 3: Action Buttons */}
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: { xs: 'stretch', md: 'flex-start' },
                                }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={<IconUserCheck />}
                                    onClick={handleAccept}
                                    disabled={
                                        selectedInvitations.length === 0 ||
                                        accepting ||
                                        selectedInvitations.every((id) => {
                                            const inv = filteredData.find(
                                                (inv) => inv.serial === id
                                            );
                                            return (
                                                inv?.invitationStatus === 'accepted' ||
                                                inv?.invitationStatus === 'rejected'
                                            );
                                        })
                                    }
                                    sx={{
                                        minWidth: { xs: '100%', sm: '200px' },
                                        height: '48px',
                                        borderRadius: '12px',
                                        background:
                                            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                        boxShadow: '0 4px 15px rgba(67, 233, 123, 0.4)',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            background:
                                                'linear-gradient(135deg, #3dd16b 0%, #32e5c7 100%)',
                                            boxShadow: '0 6px 20px rgba(67, 233, 123, 0.6)',
                                        },
                                        '&:disabled': {
                                            background: '#ccc',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    {accepting
                                        ? 'Sending Registration Links...'
                                        : `Send Registration Link (${selectedInvitations.length})`}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<IconUserX />}
                                    onClick={handleReject}
                                    disabled={
                                        selectedInvitations.length === 0 ||
                                        rejecting ||
                                        selectedInvitations.every((id) => {
                                            const inv = filteredData.find(
                                                (inv) => inv.serial === id
                                            );
                                            return (
                                                inv?.invitationStatus === 'accepted' ||
                                                inv?.invitationStatus === 'rejected'
                                            );
                                        })
                                    }
                                    sx={{
                                        minWidth: { xs: '100%', sm: '150px' },
                                        height: '48px',
                                        borderRadius: '12px',
                                        background:
                                            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                        boxShadow: '0 4px 15px rgba(250, 112, 154, 0.4)',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            background:
                                                'linear-gradient(135deg, #f85a8a 0%, #fdd030 100%)',
                                            boxShadow: '0 6px 20px rgba(250, 112, 154, 0.6)',
                                        },
                                        '&:disabled': {
                                            background: '#ccc',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    {rejecting
                                        ? 'Rejecting...'
                                        : `Reject (${selectedInvitations.length})`}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<IconRefresh />}
                                    onClick={() => refetch()}
                                    sx={{
                                        minWidth: { xs: '100%', sm: '120px' },
                                        height: '48px',
                                        borderRadius: '12px',
                                        border: '2px solid #43e97b',
                                        color: '#43e97b',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            border: '2px solid #43e97b',
                                            backgroundColor: 'rgba(67, 233, 123, 0.1)',
                                        },
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Row 4: Data Table */}
                    <Card>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={
                                                    paginatedData.length > 0 &&
                                                    selectedInvitations.length ===
                                                        paginatedData.length
                                                }
                                                indeterminate={
                                                    selectedInvitations.length > 0 &&
                                                    selectedInvitations.length <
                                                        paginatedData.length
                                                }
                                                onChange={handleSelectAll}
                                                disabled={paginatedData.every(
                                                    (inv) =>
                                                        inv.invitationStatus === 'accepted' ||
                                                        inv.invitationStatus === 'rejected'
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Company</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Response Date</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.map((invitation) => {
                                        const invitationInfo = JSON.parse(invitation.data || '{}');
                                        const isSelected = selectedInvitations.includes(
                                            invitation.serial
                                        );

                                        return (
                                            <TableRow key={invitation.serial} hover>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(event) =>
                                                            handleSelectOne(
                                                                event,
                                                                invitation.serial
                                                            )
                                                        }
                                                        disabled={
                                                            invitation.invitationStatus ===
                                                                'accepted' ||
                                                            invitation.invitationStatus ===
                                                                'rejected'
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {invitationInfo['Full Name'] ||
                                                        invitationInfo['Name'] ||
                                                        invitationInfo['First Name'] +
                                                            ' ' +
                                                            invitationInfo['Last Name'] ||
                                                        'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    {invitationInfo.Email || 'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    {invitationInfo.Company ||
                                                        invitationInfo.Organization ||
                                                        invitationInfo['Organization Name'] ||
                                                        invitationInfo['company'] ||
                                                        invitationInfo['organization'] ||
                                                        invitationInfo['organization name'] ||
                                                        invitationInfo['Company Name'] ||
                                                        'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            getDisplayStatus(invitation) ===
                                                            'accepted'
                                                                ? 'Accepted'
                                                                : getDisplayStatus(invitation) ===
                                                                  'rejected'
                                                                ? 'Rejected'
                                                                : getDisplayStatus(invitation) ===
                                                                  'accepted_email_sent'
                                                                ? 'Registration Link Sent'
                                                                : 'Pending'
                                                        }
                                                        color={getStatusColor(
                                                            getDisplayStatus(invitation)
                                                        )}
                                                        size="small"
                                                        icon={getStatusIcon(
                                                            getDisplayStatus(invitation)
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {invitation.responseTimestamp
                                                        ? new Date(
                                                              parseInt(invitation.responseTimestamp)
                                                          ).toLocaleDateString()
                                                        : 'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip
                                                            title={
                                                                invitation.invitationStatus ===
                                                                'accepted'
                                                                    ? 'Already completed registration'
                                                                    : invitation.invitationStatus ===
                                                                      'rejected'
                                                                    ? 'Already rejected'
                                                                    : 'Send Registration Link'
                                                            }
                                                        >
                                                            <span>
                                                                {/* CEO #1: action icons must always be visible; give a bordered,
                                                                    coloured button so the affordance reads even when disabled. */}
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: 'success.main',
                                                                        '&.Mui-disabled': { opacity: 0.4 }
                                                                    }}
                                                                    disabled={
                                                                        invitation.invitationStatus ===
                                                                            'accepted' ||
                                                                        invitation.invitationStatus ===
                                                                            'rejected'
                                                                    }
                                                                    onClick={() => {
                                                                        setSelectedInvitations([
                                                                            invitation.serial,
                                                                        ]);
                                                                        handleAccept();
                                                                    }}
                                                                >
                                                                    <IconCheck size={18} stroke={2.5} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={
                                                                invitation.invitationStatus ===
                                                                'accepted'
                                                                    ? 'Cannot reject - already completed registration'
                                                                    : invitation.invitationStatus ===
                                                                      'rejected'
                                                                    ? 'Already rejected'
                                                                    : 'Reject'
                                                            }
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: 'error.main',
                                                                        '&.Mui-disabled': { opacity: 0.4 }
                                                                    }}
                                                                    disabled={
                                                                        invitation.invitationStatus ===
                                                                            'accepted' ||
                                                                        invitation.invitationStatus ===
                                                                            'rejected'
                                                                    }
                                                                    onClick={() => {
                                                                        setSelectedInvitations([
                                                                            invitation.serial,
                                                                        ]);
                                                                        handleReject();
                                                                    }}
                                                                >
                                                                    <IconX size={18} stroke={2.5} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, { label: 'All', value: 'all' }]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage === 'all' ? -1 : rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(event.target.value);
                                setPage(0);
                            }}
                        />
                    </Card>
                </Stack>
            </Box>

            {/* Reject Confirmation Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
                <DialogTitle>Reject Invitations</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reject {selectedInvitations.length} invitation(s)?
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Send Reject Email</InputLabel>
                            <Select
                                value={sendRejectEmail}
                                label="Send Reject Email"
                                onChange={(e) => setSendRejectEmail(e.target.value)}
                            >
                                <MenuItem value={false}>No, just mark as rejected</MenuItem>
                                <MenuItem value={true}>Yes, send reject email</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmReject} color="error" variant="contained">
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast Notification */}
            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert
                    onClose={() => setToast({ ...toast, open: false })}
                    severity={toast.severity}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            {/* Email Preview Modal */}
            {showPreview && previewData && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <Paper
                        sx={{
                            p: 3,
                            maxWidth: { xs: '95%', sm: '700px', md: '800px' },
                            maxHeight: '85vh',
                            overflow: 'auto',
                            width: '90%',
                        }}
                    >
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="h6">
                                    {previewAction === 'accept'
                                        ? 'Accept Email Preview'
                                        : 'Reject Email Preview'}
                                </Typography>
                                <Button onClick={() => setShowPreview(false)}>Close</Button>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Total Recipients:</strong> {previewData.totalRecipients}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Recipients:</strong>
                                </Typography>
                                <Box
                                    sx={{
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                        border: '1px solid #e0e0e0',
                                        p: 1,
                                        borderRadius: 1,
                                    }}
                                >
                                    {previewData.recipientEmails?.map((email, index) => (
                                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                            {index + 1}. {email}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Subject:</strong> {previewData.subject}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Body:</strong>
                                </Typography>
                                <Box
                                    sx={{
                                        border: '1px solid #ccc',
                                        p: 2,
                                        borderRadius: 1,
                                        minHeight: 200,
                                        backgroundColor: '#f9f9f9',
                                    }}
                                >
                                    {previewData.isHtml ? (
                                        <Box
                                            dangerouslySetInnerHTML={{ __html: previewData.body }}
                                            sx={{
                                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                    color: 'text.primary',
                                                    fontWeight: 600,
                                                    mb: 1,
                                                },
                                                '& p': { mb: 1 },
                                                '& a': { color: 'primary.main' },
                                                '& strong': { fontWeight: 600 },
                                                '& em': { fontStyle: 'italic' },
                                                '& ul, & ol': { pl: 2, mb: 1 },
                                                '& li': { mb: 0.5 },
                                                '& table': {
                                                    borderCollapse: 'collapse',
                                                    width: '100%',
                                                },
                                                '& td, & th': {
                                                    border: '1px solid #ddd',
                                                    padding: '8px',
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {previewData.body}
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Email sending option for reject action */}
                            {previewAction === 'reject' && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        bgcolor: '#f9f9f9',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Checkbox
                                            checked={sendRejectEmail}
                                            onChange={(e) => setSendRejectEmail(e.target.checked)}
                                            color="primary"
                                        />
                                        <Box>
                                            <Typography variant="subtitle2">
                                                Send rejection email to attendees
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {sendRejectEmail
                                                    ? 'Email will be sent to all selected attendees'
                                                    : 'Only status will be changed to rejected, no email will be sent'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<IconSend />}
                                    onClick={
                                        previewAction === 'accept'
                                            ? handleConfirmAccept
                                            : handleConfirmReject
                                    }
                                    color={previewAction === 'accept' ? 'success' : 'error'}
                                    disabled={accepting || rejecting}
                                >
                                    {accepting || rejecting
                                        ? 'Processing...'
                                        : `Confirm & ${
                                              previewAction === 'accept' ? 'Accept' : 'Reject'
                                          }`}
                                </Button>
                                <Button variant="outlined" onClick={() => setShowPreview(false)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
            )}
        </MainCard>
    );
}
